/**
 * Model monitoring (Evidently), as the zAdmin backend serves it under
 * `/partner/{id}/…/monitoring/*`. Field comments follow the backend's own
 * descriptions; everything the service may omit is nullable.
 */

export const MONITORING_STATUSES = [
	"CRITICAL",
	"WARNING",
	"HEALTHY",
	"UNKNOWN",
] as const;
export type MonitoringStatus = (typeof MONITORING_STATUSES)[number];

export type MonitoringColor = "green" | "yellow" | "red" | "grey";

export const RECOMMENDATION_ACTIONS = [
	"retrain",
	"recalibrate",
	"monitor",
	"collect_labels",
] as const;
export type RecommendationAction = (typeof RECOMMENDATION_ACTIONS)[number];

/** Which evidence the recommendation rests on. */
export type RecommendationBasis = "measured" | "estimated" | "drift_only";

export type MonitoringTrendDirection = "improving" | "degrading" | "stable";

export type CheckTrigger = "scheduled" | "manual";

export interface TopDriftedFeature {
	feature: string;
	drift_score: number | null;
	drifted: boolean | null;
	importance: number | null;
}

/** Current state of one model — a row of the dashboard and the detail header. */
export interface MonitoringModelStatus {
	model_id: string;
	model_name: string | null;
	/** Kept as `string`: the service owns the vocabulary and may grow it. */
	status: MonitoringStatus | string;
	color: MonitoringColor | string;
	reason: string | null;
	trend: MonitoringTrendDirection | null;
	checked_at: string | null;
	window_from: string | null;
	window_to: string | null;
	report_url: string | null;
	rows_analyzed: number | null;
	low_confidence: boolean | null;
	sampled: boolean | null;
	recommendation_action: RecommendationAction | string | null;
	recommendation_based_on: RecommendationBasis | string | null;
	/** The prediction column was unusable, so drift/estimation are off. */
	prediction_column_mismatch: boolean;
	top_drifted_features: TopDriftedFeature[];
}

export interface MonitoringStatusList {
	models: MonitoringModelStatus[];
}

export interface MonitoringTrendPoint {
	checked_at: string;
	window_from: string;
	window_to: string;
	status: MonitoringStatus | string;
	color: MonitoringColor | string;
	share_of_drifted_columns: number | null;
	drift_test_failures: number | null;
	prediction_drift_score: number | null;
	estimated_roc_auc: number | null;
}

export interface MonitoringTrend {
	model_id: string;
	direction: MonitoringTrendDirection | null;
	/** Oldest first. */
	points: MonitoringTrendPoint[];
}

export interface CheckSummary {
	total_tests?: number | null;
	passed_tests?: number | null;
	failed_tests?: number | null;
	warning_tests?: number | null;
	dataset_drift_detected?: boolean | null;
	share_of_drifted_columns?: number | null;
	/** Share of SHAP importance mass that is drifting. */
	drifted_importance?: number | null;
	prediction_drift_detected?: boolean | null;
	failed_test_names?: string[];
	quality_tests_skipped?: boolean | null;
	model_quality_tests_skipped?: boolean | null;
	low_confidence?: boolean | null;
	drift_test_failures?: number | null;
	quality_test_failures?: number | null;
	model_quality_test_failures?: number | null;
}

export interface FeatureDrift {
	feature: string;
	drift_score: number | null;
	drifted: boolean | null;
	/** e.g. "K-S p_value" or "PSI" — scales differ per test. */
	stattest: string | null;
	importance: number | null;
}

export interface PredictionDrift {
	available: boolean;
	column?: string | null;
	drift_score?: number | null;
	drifted?: boolean | null;
	stattest?: string | null;
}

export interface QualityMetrics {
	available: boolean;
	task?: string | null;
	/** CBPE/DLE for an estimate; absent when measured. */
	method?: string | null;
	metrics?: Record<string, number>;
	batch_rows?: number | null;
	reference_rows?: number | null;
	note?: string | null;
}

export interface SchemaValidation {
	checked: boolean;
	coercions?: Record<string, unknown>;
	type_violations?: unknown[];
	dropped_rows?: number;
}

export interface MultivariateDrift {
	available: boolean;
	method?: string | null;
	/** Domain-classifier ROC-AUC: ~0.5 none, ~1.0 strong. */
	drift_score?: number | null;
	drifted?: boolean | null;
	threshold?: number | null;
}

export interface SegmentDrift {
	segment: string;
	value: unknown;
	rows_ref?: number | null;
	rows_cur?: number | null;
	drift_score?: number | null;
	drifted?: boolean | null;
}

export interface CheckRecommendation {
	action: RecommendationAction | string;
	reason?: string | null;
	based_on?: RecommendationBasis | string | null;
}

/** The service's full verdict for one check. */
export interface MonitoringCheckResult {
	check_id: string;
	model_id: string;
	batch_id?: string | null;
	status: MonitoringStatus | string;
	color?: MonitoringColor | string | null;
	status_changed?: boolean | null;
	previous_status?: MonitoringStatus | string | null;
	checked_at?: string | null;
	reference_version?: string | null;
	rows_received?: number | null;
	rows_analyzed?: number | null;
	sampled?: boolean | null;
	summary?: CheckSummary | null;
	feature_drift?: FeatureDrift[];
	prediction_drift?: PredictionDrift | null;
	model_quality?: QualityMetrics | null;
	estimated_quality?: QualityMetrics | null;
	schema_validation?: SchemaValidation | null;
	multivariate_drift?: MultivariateDrift | null;
	segment_drift?: SegmentDrift[];
	recommendation?: CheckRecommendation | null;
	report_url?: string | null;
}

/** One stored verdict, exactly as recorded. */
export interface MonitoringCheckRecord {
	id: number;
	check_id: string;
	batch_id: string | null;
	window_from: string;
	window_to: string;
	trigger: CheckTrigger | string;
	status: MonitoringStatus | string;
	previous_status: MonitoringStatus | string | null;
	status_changed: boolean | null;
	recommendation_action: RecommendationAction | string | null;
	recommendation_based_on: RecommendationBasis | string | null;
	rows_sent: number | null;
	rows_analyzed: number | null;
	report_url: string | null;
	summary: CheckSummary | null;
	/** The service response in full; null for checks stored before it was kept. */
	response: MonitoringCheckResult | null;
	created_at: string;
}

export interface MonitoringCheckList {
	total: number;
	limit: number;
	offset: number;
	checks: MonitoringCheckRecord[];
}

export interface MonitoringCheckRequest {
	date_from: string;
	date_to: string;
	batch_id?: string | null;
	segment_by?: string[] | null;
}

/**
 * The 18 thresholds the service lets us tune per model. Every field optional;
 * only the ones a caller names are sent (and merged) upstream.
 */
export interface MonitoringThresholds {
	drift_share_warning?: number | null;
	drift_share_critical?: number | null;
	min_rows_for_verdict?: number | null;
	min_rows_for_quality_tests?: number | null;
	min_rows_for_model_quality?: number | null;
	max_analysis_rows?: number | null;
	num_stattest?: string | null;
	cat_stattest?: string | null;
	num_stattest_threshold?: number | null;
	cat_stattest_threshold?: number | null;
	min_roc_auc?: number | null;
	min_gini?: number | null;
	max_ece?: number | null;
	min_r2?: number | null;
	max_auc_drop?: number | null;
	multivariate_drift_threshold?: number | null;
	min_estimated_roc_auc?: number | null;
	max_drifted_importance?: number | null;
}

export type MonitoringThresholdKey = keyof MonitoringThresholds;

/** Values are whatever the service holds — numbers or test names. */
export type MonitoringConfigValues = Record<string, unknown>;

export interface MonitoringGlobalConfig {
	/** Stored global overrides. */
	config: MonitoringConfigValues;
	/** Env defaults with the overrides applied — what is in force. */
	effective: MonitoringConfigValues;
}

export interface MonitoringModelConfig {
	model_id: string;
	config: MonitoringThresholds;
	/** Model overrides merged over the global defaults. */
	effective: MonitoringConfigValues;
}

export interface MonitoringRecipients {
	model_id: string;
	emails: string[];
}

export interface MonitoringRegistration {
	model_id: string;
	partner_id: string | null;
	reference_version: string | null;
	/** Query string (the SAS) redacted. */
	reference_uri: string | null;
	original_rows: number | null;
	stored_rows: number | null;
	sampled: boolean | null;
	features: string[];
	reference_metrics: Record<string, unknown>;
	column_mapping: Record<string, unknown>;
	registered_at: string | null;
	model_version: string | null;
	author_login: string | null;
}
